/**
 * User Action Atoms - Auth and Profile operations
 *
 * These atoms handle all user-related mutations (auth, profile updates).
 */

import { atom } from "jotai";
import { createClient } from "@/backend/lib/supabase/client";
import type { UpdateProfileBody, Profile, SystemRole } from "@/isomorphic/schemas";
import {
  userAtom,
  sessionAtom,
  accessTokenAtom,
  authStatusAtom,
  currentProfileAtom,
  profileLoadingStatusAtom,
  profileErrorCodeAtom,
  userWorkspacesAtom,
  userWorkspacesStatusAtom,
  adminProfilesAtom,
  adminProfilesStatusAtom,
  userRoleAtom,
} from "../core/user";
import { API_ROUTES } from "@/frontend/lib/api/routes";

// ============================================================================
// AUTH ACTIONS
// ============================================================================

/**
 * Initialize session - call at app start
 */
export const initSessionAtom = atom(null, async (get, set) => {
  try {
    const supabase = createClient();
    if (!supabase) {
      console.error("[UserActions] initSession, supabase is not initialized");
      return { error: "supabase is not initialized", needReload: true };
    }

    const { data, error: claimsError } = await supabase.auth.getClaims();

    if (claimsError) {
      console.error("[UserActions] initSession claimsError", claimsError);
      return { error: claimsError, needReload: true };
    }

    if (!data?.claims) {
      console.error("[UserActions] initSession no claims");
      return null;
    }

    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError || !userData?.user) {
      console.error("[UserActions] initSession userError", userError);
      set(authStatusAtom, "unauthenticated");
      return { error: userError };
    }

    // Get the access token
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token || null;

    set(userAtom, userData.user);
    set(sessionAtom, data.claims);
    set(accessTokenAtom, accessToken);
    set(authStatusAtom, "authenticated");
    set(userRoleAtom, null);

    return { user: userData.user, claims: data.claims, accessToken };
  } catch (error) {
    console.error("[UserActions] Error initializing session:", error);
    set(authStatusAtom, "unauthenticated");
    return { error };
  }
});

/**
 * Sign out
 */
export const signOutAtom = atom(null, async (get, set) => {
  try {
    const supabase = createClient();
    await supabase.auth.signOut();

    set(userAtom, null);
    set(sessionAtom, null);
    set(accessTokenAtom, null);
    set(authStatusAtom, "unauthenticated");
    set(currentProfileAtom, null);
    set(userRoleAtom, null);
  } catch (error) {
    console.error("[UserActions] Error signing out:", error);
  }
});

/**
 * Fetch current user's system role.
 * AuthProvider is the canonical bootstrap owner for this request.
 */
export const fetchCurrentUserRoleAtom = atom(null, async (_get, set, jwt: string | null) => {
  if (!jwt) {
    set(userRoleAtom, null);
    return null;
  }

  try {
    const roleResponse = await fetch(API_ROUTES.user.getRole, {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });

    if (!roleResponse.ok) {
      throw new Error("Failed to fetch role");
    }

    const roleData = (await roleResponse.json()) as { data?: { role?: SystemRole } };
    const role = roleData.data?.role || "user";
    set(userRoleAtom, role);
    return role;
  } catch (error) {
    console.error("[UserActions] Error fetching user role:", error);
    set(userRoleAtom, "user");
    return "user";
  }
});

// ============================================================================
// PROFILE ACTIONS
// ============================================================================

/**
 * Fetch current user profile
 */
export const fetchCurrentProfileAtom = atom(null, async (get, set, jwt: string) => {
  try {
    if (!jwt) {
      return null;
    }

    set(profileLoadingStatusAtom, "loading");

    const response = await fetch(API_ROUTES.profile.get, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      // 404 means no profile exists - this is normal for new users
      if (response.status === 404) {
        console.log("[UserActions] No profile found - user needs onboarding");
        set(currentProfileAtom, null);
        set(profileLoadingStatusAtom, "success");
        set(profileErrorCodeAtom, null);
        return null;
      }

      // 401 means invalid/expired JWT or user doesn't exist
      // This happens when JWT is cached but user was deleted (e.g., after db reset)
      // Set error status to trigger session cleanup in useProfileGuard
      if (response.status === 401) {
        console.log("[UserActions] Unauthorized - invalid or expired session");
        set(currentProfileAtom, null);
        set(profileLoadingStatusAtom, "error");
        set(profileErrorCodeAtom, 401);
        throw new Error("Unauthorized - invalid session");
      }

      // Other errors (network, 500, etc.)
      set(profileErrorCodeAtom, response.status);

      // Check if response is JSON before parsing
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const error = await response.json();
        throw new Error(error.error || `Failed to fetch profile: ${response.status}`);
      } else {
        // HTML error page or other non-JSON response
        const text = await response.text();
        console.error("[UserActions] Non-JSON response:", text.substring(0, 200));
        throw new Error(`Failed to fetch profile: ${response.status} ${response.statusText}`);
      }
    }

    const { data } = await response.json();
    set(currentProfileAtom, data);
    set(profileLoadingStatusAtom, "success");
    set(profileErrorCodeAtom, null);
    return data;
  } catch (error) {
    console.error("[UserActions] Error fetching profile:", error);
    set(profileLoadingStatusAtom, "error");
    // profileErrorCodeAtom already set in the if (!response.ok) block
    return null;
  }
});

/**
 * Save profile (create or update)
 * Helper function for onboarding and profile updates
 */
export const saveProfile = async (
  jwt: string,
  profileData: Partial<UpdateProfileBody>,
  historyVersion?: number,
): Promise<Profile> => {
  try {
    if (!jwt) {
      throw new Error("No authentication token");
    }

    // Use create endpoint if no historyVersion (new profile)
    // Use update endpoint if historyVersion exists (existing profile)
    const isCreate = historyVersion === undefined;
    const endpoint = isCreate ? API_ROUTES.profile.create : API_ROUTES.profile.update;

    // Filter out empty strings and convert to undefined for optional fields
    const cleanedData = Object.fromEntries(
      Object.entries(profileData).filter(([_, value]) => value !== "" && value !== null),
    );

    const body = isCreate ? cleanedData : { historyVersion, ...cleanedData };

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      // Check if response is JSON before parsing
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const error = await response.json();

        // If we tried to create but profile already exists, try to update instead
        if (isCreate && response.status === 500 && error.error?.includes("already exists")) {
          console.log("[UserActions] Profile already exists, fetching and updating instead");

          // Fetch existing profile to get historyVersion
          const getResponse = await fetch(API_ROUTES.profile.get, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${jwt}`,
              "Content-Type": "application/json",
            },
          });

          if (getResponse.ok) {
            const { data: existingProfile } = await getResponse.json();
            // Retry with update endpoint
            return saveProfile(jwt, profileData, existingProfile.historyVersion);
          }
        }

        throw new Error(error.error || `Failed to save profile: ${response.status}`);
      } else {
        // HTML error page or other non-JSON response
        const text = await response.text();
        console.error("[UserActions] Non-JSON response:", text.substring(0, 200));
        throw new Error(`Failed to save profile: ${response.status} ${response.statusText}`);
      }
    }

    const { data } = await response.json();
    console.log("[UserActions] Profile saved:", data);
    return data;
  } catch (error) {
    console.error("[UserActions] Error saving profile:", error);
    throw error;
  }
};

/**
 * Save profile (create or update) and update atom
 * This is the recommended action to use for saving profiles
 */
export const saveProfileAtom = atom(
  null,
  async (get, set, args: { jwt: string; data: Partial<UpdateProfileBody>; historyVersion?: number }) => {
    const { jwt, data: profileData, historyVersion } = args;
    try {
      const savedProfile = await saveProfile(jwt, profileData, historyVersion);
      set(currentProfileAtom, savedProfile);
      console.log("[UserActions] Profile saved and atom updated:", savedProfile);
      return savedProfile;
    } catch (error) {
      console.error("[UserActions] Error saving profile:", error);
      throw error;
    }
  },
);

// ============================================================================
// USER WORKSPACES ACTIONS
// ============================================================================

/**
 * Fetch user workspaces
 */
export const fetchUserWorkspacesAtom = atom(null, async (get, set, jwt: string) => {
  try {
    if (!jwt) {
      return;
    }

    set(userWorkspacesStatusAtom, "loading");

    const response = await fetch(API_ROUTES.user.getWorkspaces, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch workspaces");
    }

    const { data } = await response.json();
    set(userWorkspacesAtom, data || []);
    set(userWorkspacesStatusAtom, "success");
  } catch (error) {
    console.error("[UserActions] Error fetching user workspaces:", error);
    set(userWorkspacesStatusAtom, "error");
  }
});

// ============================================================================
// ADMIN ACTIONS
// ============================================================================

/**
 * Fetch all profiles (admin only)
 */
export const fetchAllProfilesAtom = atom(null, async (get, set, jwt: string) => {
  try {
    if (!jwt) {
      return;
    }

    set(adminProfilesStatusAtom, "loading");

    const response = await fetch(API_ROUTES.admin.profiles, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch profiles");
    }

    const { data } = await response.json();
    set(adminProfilesAtom, data || []);
    set(adminProfilesStatusAtom, "success");
  } catch (error) {
    console.error("[UserActions] Error fetching all profiles:", error);
    set(adminProfilesStatusAtom, "error");
  }
});
